import React, { useEffect, useRef } from 'react';
import { RefreshCw, AlertTriangle, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FormRouteEnum, useFormRouting } from '../../routes/FormRoutes';
import type { FormData } from '../../types/form';
import type { AlternativeMethodCallbacks } from '../../types/verification';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import ErrorMessage from '../ui/ErrorMessage';
import TryAnotherWay from '../ui/TryAnotherWay';
import ActionLink from '../ui/ActionLink';
import ConsentCheckbox from '../ui/ConsentCheckbox';
import { storageController } from '../../controllers/StorageController';
import { useHubspot } from '../../hooks/useHubspot';

type PhoneVerificationFormProps = AlternativeMethodCallbacks & {
  formData: FormData;
  loading: boolean;
  error: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  onResendCode?: () => Promise<void>;
  onReenterInfo?: () => void;
  smsAttempts: number;
  methodExhausted?: boolean;
};

export default function PhoneVerificationForm({
  formData,
  loading,
  error,
  onChange,
  onSubmit,
  onResendCode,
  onReenterInfo,
  smsAttempts,
  methodExhausted = false,
  onChooseKba,
  onChooseProspect,
}: PhoneVerificationFormProps) {
  const { t, i18n } = useTranslation('wizard');
  const { navigateToRoute } = useFormRouting();
  const hubspot = useHubspot();
  
  // Redirect if not successful
  if (!storageController.isApplicationSmsCode()) {
    navigateToRoute(FormRouteEnum.START);
    return null;
  }
  
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Track verification attempt in HubSpot
    hubspot.trackEvent('phone_verification_attempted', {
      email: formData.email,
      form_step: 'phone_verification',
      phone: formData.phone,
      verification_attempts: smsAttempts
    });

    await onSubmit(e);
  };

  const handleResendCode = async () => {
    if (onResendCode) {
      // Track resend code in HubSpot
      hubspot.trackEvent('verification_code_resent', {
        email: formData.email,
        form_step: 'phone_verification',
        phone: formData.phone,
        verification_attempts: smsAttempts
      });

      await onResendCode();
    }
  };

  const handleReenterInfo = () => {
    if (onReenterInfo) {
      // Track info reentry in HubSpot
      hubspot.trackEvent('phone_verification_reenter', {
        email: formData.email,
        form_step: 'phone_verification',
        phone: formData.phone,
        verification_attempts: smsAttempts
      });

      onReenterInfo();
    }
  };


  const handlePromoCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;

    onChange({
      ...e,
      target: {
        ...e.target,
        name: 'promoSmsConsent',
        value: isChecked.toString()
      }
    });

    onChange({
      ...e,
      target: {
        ...e.target,
        name: 'promoSmsConsentDateTime',
        value: isChecked ? new Date().toISOString() : ''
      }
    });
  };

  const isValidCode = (code: string) => {
    return /^\d{6}$/.test(code);
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
    const date = new Date(year, month - 1, day);
    return new Intl.DateTimeFormat(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  return (
    <>
      <form id="phone-verification-form" name="phone-verification-form" onSubmit={handleSubmit} className="space-y-6">
        <div className="text-center">
          <div className="flex flex-col items-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[#212d52]/10 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
              <div className="relative w-16 h-16 rounded-full bg-[#212d52]/10 flex items-center justify-center animate-[bounce_2s_ease-in-out_infinite]">
                <MessageCircle className="w-8 h-8 text-[#212d52]" />
              </div>
            </div>
          </div>
          <Title 
            id="phone-verification-title"
            name="phone-verification-title"
            as="h1" 
            className="mb-2"
          >
            {t('phoneVerification.title')}
          </Title>
          <Description 
            id="phone-verification-description"
            name="phone-verification-description"
            size="lg"
          >
            {t('phoneVerification.description')}
          </Description>
        </div>
  
        <div>
          <input
            ref={inputRef}
            type="text"
            name="smsCode"
            id="phone-verification-code"
            value={formData.smsCode}
            onChange={onChange}
            placeholder="000000"
            maxLength={6}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#b3905e] focus:ring-2 focus:ring-[#b3905e]/20 transition duration-200 text-center text-2xl tracking-widest"
            inputMode="numeric"
            pattern="\d{6}"
          />
        </div>
  
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{t('phoneVerification.mobilePhoneLabel')}</span>
              <span className="font-medium text-gray-900">{formData.phone}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{t('phoneVerification.birthDateLabel')}</span>
              <span className="font-medium text-gray-900">{formatDate(formData.birthDate)}</span>
            </div>
          </div>
        </div>

        <ConsentCheckbox
          id="phone-verification-promo-consent"
          name="promoSmsConsent"
          checked={formData.promoSmsConsent || false}
          onChange={handlePromoCheckboxChange}
        >
          {t('phoneVerification.promoConsent')}
        </ConsentCheckbox>
  
        <ErrorMessage message={error} />

        <TryAnotherWay
          show={!!error || methodExhausted}
          onChooseKba={onChooseKba}
          onChooseProspect={onChooseProspect}
        />

        {!methodExhausted && (
          <Button
            id="phone-verification-submit"
            name="phone-verification-submit"
            type="submit"
            disabled={loading || !isValidCode(formData.smsCode || '')}
            loading={loading}
            loadingText={t('phoneVerification.verifying')}
            fullWidth
            size="lg"
          >
            {t('phoneVerification.submit')}
          </Button>
        )}
      </form>

      <div className="pt-4 border-t border-gray-200">
        <div className="flex flex-col items-center gap-3">
          {!methodExhausted && (
            <ActionLink
              id="phone-verification-resend"
              name="phone-verification-resend"
              onClick={handleResendCode}
              icon={RefreshCw}
              align="center"
            >
              {t('phoneVerification.resend')}
            </ActionLink>
          )}

          <ActionLink
            id="phone-verification-reenter"
            name="phone-verification-reenter"
            onClick={handleReenterInfo}
            icon={AlertTriangle}
            align="center"
          >
            {t('phoneVerification.reenter')}
          </ActionLink>
        </div>
      </div>
    </>
  );
}